'use client';

/**
 * AddConnectionModal Component
 *
 * Modal for adding a new connection to an organization.
 * Currently supports GitHub OAuth, with other providers coming soon.
 */

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Inline,
  Input,
  Label,
  Stack,
  Text,
} from '@cortex/ui';
import { Github } from 'lucide-react';
import * as React from 'react';

import { API_BASE_URL } from '@/hooks/use-api';

interface AddConnectionModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Organization ID to add connection to */
  organizationId: string;
  /** Callback when connection is successfully added */
  onSuccess?: () => void;
}

type Provider = {
  id: string;
  name: string;
  icon: React.ReactNode;
  enabled: boolean;
};

const PROVIDERS: Provider[] = [
  {
    id: 'github',
    name: 'GitHub',
    icon: <Github className="h-5 w-5" />,
    enabled: true,
  },
  {
    id: 'jira',
    name: 'Jira',
    icon: <Stack className="bg-muted-foreground/40 h-5 w-5 rounded" />,
    enabled: false,
  },
];

export function AddConnectionModal({
  open,
  onClose,
  organizationId,
  onSuccess: _onSuccess,
}: AddConnectionModalProps) {
  const [step, setStep] = React.useState<'select' | 'configure'>('select');
  const [selectedProvider, setSelectedProvider] = React.useState<string | null>(
    null
  );
  const [label, setLabel] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [connecting, setConnecting] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setStep('select');
      setSelectedProvider(null);
      setLabel('');
      setDescription('');
      setConnecting(false);
    }
  }, [open]);

  const handleProviderSelect = (providerId: string) => {
    setSelectedProvider(providerId);
    setStep('configure');
  };

  const handleBack = () => {
    setStep('select');
    setSelectedProvider(null);
    setLabel('');
    setDescription('');
  };

  const handleConnect = () => {
    if (!selectedProvider || !label) return;

    setConnecting(true);

    const url = `${API_BASE_URL}/admin/connections/${selectedProvider}/authorize?organizationId=${encodeURIComponent(organizationId)}&label=${encodeURIComponent(label)}`;

    if (description) {
      window.location.href = `${url}&description=${encodeURIComponent(description)}`;
    } else {
      window.location.href = url;
    }
  };

  const selectedProviderData = PROVIDERS.find((p) => p.id === selectedProvider);

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {step === 'select'
              ? 'Add Connection'
              : `Connect ${selectedProviderData?.name}`}
          </DialogTitle>
          <DialogDescription>
            Connect your organization to external providers
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {step === 'select' && (
            <Stack gap="md">
              <Text color="muted" size="sm">
                Select a provider to connect to your organization
              </Text>
              {PROVIDERS.map((provider) => (
                <Button
                  key={provider.id}
                  variant="ghost"
                  onClick={() =>
                    provider.enabled && handleProviderSelect(provider.id)
                  }
                  disabled={!provider.enabled}
                  className="group hover:border-primary/20 relative flex h-auto w-full items-center gap-5 rounded-lg border border-white/8 bg-white/3 p-5 text-left transition-all duration-150 hover:bg-white/6 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-white/8 disabled:hover:bg-white/3"
                >
                  <Inline className="group-hover:border-primary/30 group-hover:from-primary/20 group-hover:to-primary/10 flex h-14 w-14 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-white/10 to-white/5 shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_2px_8px_rgba(0,0,0,0.2)] transition-colors duration-100 group-hover:shadow-md">
                    {provider.icon}
                  </Inline>
                  <Stack className="flex-1" gap="none">
                    <Text
                      weight="semibold"
                      size="base"
                      className="group-hover:text-foreground"
                    >
                      {provider.name}
                    </Text>
                    {!provider.enabled && (
                      <Text size="sm" color="muted" className="mt-0.5">
                        Coming soon
                      </Text>
                    )}
                  </Stack>
                  {provider.enabled && (
                    <Badge
                      variant="outline"
                      className="group-hover:bg-primary/15 group-hover:text-primary-light bg-white/5 px-3.5 py-1.5 text-base font-medium text-white/60 transition-colors duration-100"
                    >
                      Connect
                    </Badge>
                  )}
                </Button>
              ))}
            </Stack>
          )}

          {step === 'configure' && selectedProviderData && (
            <Stack gap="lg">
              <Stack gap="xs">
                <Label htmlFor="connectionLabel">Label</Label>
                <Input
                  id="connectionLabel"
                  type="text"
                  placeholder={`Production ${selectedProviderData.name}`}
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                />
                <Text color="muted" size="xs" className="mt-2">
                  A friendly name to identify this connection
                </Text>
              </Stack>

              <Stack gap="xs">
                <Label htmlFor="connectionDescription">
                  Description (Optional)
                </Label>
                <Input
                  id="connectionDescription"
                  type="text"
                  placeholder="Production environment access"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
                <Text color="muted" size="xs" className="mt-2">
                  Additional context about this connection
                </Text>
              </Stack>

              <Alert className="border-primary/20 from-primary/10 rounded-xl bg-gradient-to-br to-transparent">
                <AlertDescription className="text-primary-light">
                  You&apos;ll be redirected to {selectedProviderData.name} to
                  authorize this connection. Make sure you&apos;re signed in to
                  the correct account.
                </AlertDescription>
              </Alert>
            </Stack>
          )}
        </DialogBody>
        <DialogFooter>
          {step === 'configure' && (
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={connecting}
            >
              Back
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={connecting}>
            Cancel
          </Button>
          {step === 'configure' && (
            <Button
              onClick={handleConnect}
              disabled={!label}
              loading={connecting}
            >
              Connect with {selectedProviderData?.name}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
